// ═══════════════════════════════════════════════════════════════════════════
// PAGE VALIDATION RETRAIT - Mobile First
// ═══════════════════════════════════════════════════════════════════════════
// À placer dans: /opt/farm-booking/frontend/src/pages/RetraitValidation.jsx
// Page appelée quand le fermier scanne un QR code
// URL: /retrait/:code_client/:numero_reservation
// Papy 73+ - Farm-Booking
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, XCircle, Package, User, Phone, Mail, 
  Calendar, Clock, AlertTriangle, Loader2, ArrowLeft,
  Scale, Euro
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const RetraitValidation = () => {
  const { code_client, numero_reservation } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [lignesModifiees, setLignesModifiees] = useState({});

  // Charger les données de la réservation
  useEffect(() => {
    loadReservation();
  }, [code_client, numero_reservation]);

  const loadReservation = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/retrait/${code_client}/${numero_reservation}`);
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Réservation non trouvée');
      }

      setData(result.data);

      // Si déjà retirée
      if (result.data.reservation.deja_retiree) {
        setValidated(true);
      }

    } catch (err) {
      console.error('Erreur chargement:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Modifier le poids réel d'une ligne
  const handlePoidsChange = (ligneId, nouveauPoids) => {
    setLignesModifiees(prev => ({
      ...prev,
      [ligneId]: parseFloat(nouveauPoids) || 0
    }));
  };

  // Calculer le total avec les modifications
  const calculerTotal = () => {
    if (!data?.lignes) return 0;
    
    return data.lignes.reduce((sum, ligne) => {
      const qte = lignesModifiees[ligne.id] !== undefined 
        ? lignesModifiees[ligne.id] 
        : (ligne.quantite_reelle || ligne.quantite_reservee);
      return sum + (qte * ligne.prix_unitaire);
    }, 0);
  };

  // Valider le retrait
  const handleValider = async () => {
    if (validating) return;

    try {
      setValidating(true);

      // Préparer les lignes modifiées
      const lignesPayload = Object.entries(lignesModifiees).map(([id, qte]) => ({
        id: parseInt(id),
        quantite_reelle: qte
      }));

      const response = await fetch(
        `${API_BASE}/retrait/${code_client}/${numero_reservation}/valider`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lignes_modifiees: lignesPayload })
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Erreur de validation');
      }

      setValidated(true);

      // Vibration de succès (mobile)
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }

    } catch (err) {
      console.error('Erreur validation:', err);
      setError(err.message);
    } finally {
      setValidating(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU : Chargement
  // ═══════════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-green-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement de la commande...</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU : Erreur
  // ═══════════════════════════════════════════════════════════════════════════
  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Erreur</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl font-medium"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU : Validé avec succès
  // ═══════════════════════════════════════════════════════════════════════════
  if (validated) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center">
          <div className="bg-green-100 rounded-full p-4 w-24 h-24 mx-auto mb-4 flex items-center justify-center">
            <CheckCircle2 className="h-16 w-16 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-green-800 mb-2">
            Commande retirée !
          </h1>
          <p className="text-gray-600 mb-2">
            {data?.client?.nom_prenom}
          </p>
          <p className="text-lg font-bold text-green-600 mb-6">
            {calculerTotal().toFixed(2)} €
          </p>
          <p className="text-sm text-gray-500">
            {numero_reservation}
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU : Formulaire de validation
  // ═══════════════════════════════════════════════════════════════════════════
  const { reservation, client, lignes } = data;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-green-600 text-white p-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <Package className="h-8 w-8" />
            <div>
              <h1 className="font-bold text-lg">Validation Retrait</h1>
              <p className="text-green-100 text-sm">{numero_reservation}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Carte Client */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-blue-100 rounded-full p-2">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800">{client.nom_prenom}</h2>
              <p className="text-sm text-gray-500">{client.code_client}</p>
            </div>
          </div>
          
          {client.telephone && (
            <a 
              href={`tel:${client.telephone}`}
              className="flex items-center gap-2 text-gray-600 mb-1"
            >
              <Phone className="h-4 w-4" />
              <span>{client.telephone}</span>
            </a>
          )}
          
          {client.email && (
            <div className="flex items-center gap-2 text-gray-600">
              <Mail className="h-4 w-4" />
              <span className="text-sm">{client.email}</span>
            </div>
          )}
        </div>

        {/* Infos réservation */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center gap-2 text-gray-700 mb-2">
            <Calendar className="h-4 w-4" />
            <span>Retrait prévu : <strong>{formatDate(reservation.date_retrait_prevue)}</strong></span>
          </div>
          {reservation.creneau_retrait_code && (
            <div className="flex items-center gap-2 text-gray-700">
              <Clock className="h-4 w-4" />
              <span>Créneau : <strong>{reservation.creneau_retrait_code.replace('_', ' ')}</strong></span>
            </div>
          )}
          {reservation.notes_client && (
            <div className="mt-3 p-2 bg-yellow-50 rounded-lg text-sm text-yellow-800">
              💬 {reservation.notes_client}
            </div>
          )}
        </div>

        {/* Liste des produits */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b">
            <h3 className="font-bold text-gray-700">
              🛒 Panier ({lignes.length} article{lignes.length > 1 ? 's' : ''})
            </h3>
          </div>
          
          <div className="divide-y">
            {lignes.map((ligne) => {
              const qteAffichee = lignesModifiees[ligne.id] !== undefined 
                ? lignesModifiees[ligne.id] 
                : (ligne.quantite_reelle || ligne.quantite_reservee);
              const sousTotal = qteAffichee * ligne.prix_unitaire;

              return (
                <div key={ligne.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-medium text-gray-800">{ligne.designation}</h4>
                      <p className="text-sm text-gray-500">
                        {ligne.prix_unitaire.toFixed(2)} € / {ligne.unite}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{sousTotal.toFixed(2)} €</p>
                    </div>
                  </div>
                  
                  {/* Modification poids - Boutons +/- ergonomiques */}
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600 flex items-center gap-1">
                        <Scale className="h-4 w-4" />
                        Qté réelle :
                      </span>
                      <span className="text-lg font-bold text-green-600">
                        {qteAffichee.toFixed(2)} {ligne.unite}
                      </span>
                    </div>
                    
                    {/* Boutons de modification rapide */}
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      {/* Gros moins */}
                      <button
                        type="button"
                        onClick={() => handlePoidsChange(ligne.id, Math.max(0, qteAffichee - 1))}
                        className="w-12 h-12 bg-red-100 text-red-600 rounded-lg font-bold text-xl active:bg-red-200"
                      >
                        -1
                      </button>
                      {/* Petit moins */}
                      <button
                        type="button"
                        onClick={() => handlePoidsChange(ligne.id, Math.max(0, qteAffichee - 0.1))}
                        className="w-12 h-12 bg-orange-100 text-orange-600 rounded-lg font-bold text-lg active:bg-orange-200"
                      >
                        -.1
                      </button>
                      
                      {/* Input direct (cliquable pour saisie clavier) */}
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={qteAffichee.toFixed(2)}
                        onChange={(e) => handlePoidsChange(ligne.id, e.target.value)}
                        className="w-20 h-12 px-2 border-2 border-gray-300 rounded-lg text-center font-bold text-lg focus:border-green-500 focus:outline-none"
                      />
                      
                      {/* Petit plus */}
                      <button
                        type="button"
                        onClick={() => handlePoidsChange(ligne.id, qteAffichee + 0.1)}
                        className="w-12 h-12 bg-green-100 text-green-600 rounded-lg font-bold text-lg active:bg-green-200"
                      >
                        +.1
                      </button>
                      {/* Gros plus */}
                      <button
                        type="button"
                        onClick={() => handlePoidsChange(ligne.id, qteAffichee + 1)}
                        className="w-12 h-12 bg-green-200 text-green-700 rounded-lg font-bold text-xl active:bg-green-300"
                      >
                        +1
                      </button>
                    </div>
                    
                    {/* Indication si modifié */}
                    {parseFloat(ligne.quantite_reservee).toFixed(2) !== parseFloat(qteAffichee).toFixed(2) && (
                      <p className="text-xs text-orange-600 text-center mt-2">
                        Réservé : {parseFloat(ligne.quantite_reservee).toFixed(2)} {ligne.unite}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total */}
          <div className="bg-green-50 px-4 py-4 border-t">
            <div className="flex justify-between items-center">
              <span className="font-bold text-gray-700 flex items-center gap-2">
                <Euro className="h-5 w-5" />
                TOTAL
              </span>
              <span className="text-2xl font-bold text-green-600">
                {calculerTotal().toFixed(2)} €
              </span>
            </div>
            {calculerTotal() !== data.totaux.estime && (
              <p className="text-sm text-gray-500 mt-1">
                Estimé : {data.totaux.estime.toFixed(2)} €
              </p>
            )}
          </div>
        </div>

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Boutons action */}
        <div className="grid grid-cols-2 gap-3 pb-6">
          <button
            onClick={() => window.history.back()}
            className="bg-gray-200 text-gray-700 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
          >
            <XCircle className="h-6 w-6" />
            Annuler
          </button>
          
          <button
            onClick={handleValider}
            disabled={validating}
            className="bg-green-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:bg-gray-400"
          >
            {validating ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <CheckCircle2 className="h-6 w-6" />
            )}
            {validating ? 'Validation...' : 'VALIDER'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RetraitValidation;